<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="100%">
        <v-card>
            <v-card-title>
                <span class="headline" style="color: #4a4a4a">{{ $t('ticket.createDialog.title') }}</span>
            </v-card-title>

            <v-card-text>
                <v-container>
                    <v-form ref="gateForm" v-model="gateFormValido">
                        <v-select
                            :value="editedItem.id_tipo_ticket"
                            @input="onTipoTicketGateChange($event)"
                            :items="tiposTickets"
                            item-text="nome_tipo_ticket"
                            item-value="id_tipo_ticket"
                            :label="$t('ticket.createDialog.fields.tipoTicket.label')"
                            :rules="[v => !!v || $t('ticket.createDialog.fields.validation.tipoTicket')]"
                            class="mb-4"
                        />
                    </v-form>

                    <template v-if="editedItem.id_tipo_ticket && !isAgencyType && !isSuporteSistemaType">
                        <v-form ref="form" v-model="formValidado">
                            <v-tabs v-model="activeTab" color="primary" slider-color="primary" class="mb-4">
                                <v-tab>{{ $t('ticket.createDialog.tabs.geral') }}</v-tab>
                                <v-tab>{{ $t('ticket.createDialog.tabs.equipamentos') }}</v-tab>
                            </v-tabs>

                            <v-tabs-items v-model="activeTab">
                                <v-tab-item>
                                    <ticket-create-geral-fields
                                        :screen="screen"
                                        :value="editedItem"
                                        @input="$emit('update:editedItem', $event)"
                                        :categorias="categorias"
                                        :checklists="checklists"
                                        :clientes="clientes"
                                        :use-clientes="useClientes"
                                        :empresa-value="empresaValue"
                                        @update:empresa="$emit('update:empresa', $event)"
                                        :current-user="currentUser"
                                        :display-date="displayDate"
                                        :display-time="displayTime"
                                        @update:displayDate="$emit('update:displayDate', $event)"
                                        @update:displayTime="$emit('update:displayTime', $event)"
                                        :show-date-time-fields="showDateTimeFields"
                                        :tipos-tickets="tiposTickets"
                                        :hide-tipo-ticket-field="true"
                                    />
                                </v-tab-item>
                                <v-tab-item>
                                    <v-alert v-if="selectedServiceUsaVeiculo" type="info" dense class="mb-3" border="left">
                                        {{ $t('ticket.createDialog.equipamentos.csvHint') }}
                                    </v-alert>
                                    <ticket-create-equipamentos-fields
                                        :empresa="selectedIdEmpresa"
                                        :usa-veiculo="selectedServiceUsaVeiculo"
                                        :usa-equipamento="selectedServiceUsaEquipamento"
                                        :usa-kit="selectedServiceUsaKit"
                                        :vehicle-equipment-pairs="vehicleEquipmentPairs"
                                        :equipamentos-filtrados-por-empresa="equipamentosFiltradosPorEmpresa"
                                        :veiculos-filtrados-por-empresa="veiculosFiltradosPorEmpresa"
                                        :loading-equipamentos="loadingEquipamentos"
                                        :loading-veiculos="loadingVeiculos"
                                        :vehicle-equipment-pairs-valid="vehicleEquipmentPairsValid"
                                        :get-equipamento-display-text-by-id="getEquipamentoDisplayTextById"
                                        :token="token"
                                        @add-pair="$emit('add-pair')"
                                        @remove-pair="$emit('remove-pair', $event)"
                                        @duplicate-pair="$emit('duplicate-pair', $event)"
                                        @update:pairs="$emit('update:vehicleEquipmentPairs', $event)"
                                        @csv-errors-open="$emit('update:csvErrorsOpen', $event)"
                                    />
                                </v-tab-item>
                            </v-tabs-items>
                        </v-form>
                    </template>

                    <ticket-solicitacao-candidatos
                        v-else-if="isAgencyType"
                        ref="agencyForm"
                        :embedded="true"
                        :hide-submit-button="true"
                        @saved="onAgencySaved"
                        @update:loadingSave="$emit('update:loadingSave', $event)"
                    />

                    <ticket-suporte-sistema
                        v-else-if="isSuporteSistemaType"
                        ref="suporteSistemaForm"
                        :embedded="true"
                        :hide-submit-button="true"
                        @saved="onSuporteSistemaSaved"
                        @update:loadingSave="$emit('update:loadingSave', $event)"
                    />
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary darken-1" class="white--text" @click="$emit('input', false)">
                    {{ $t('button.cancel') }}
                </v-btn>
                <v-btn
                    color="primary darken-1"
                    class="white--text"
                    @click="onSave"
                    :loading="loadingSave"
                    :disabled="saveButtonDisabled"
                >
                    {{ $t('button.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import TicketCreateGeralFields from './TicketCreateGeralFields.vue';
import TicketCreateEquipamentosFields from './TicketCreateEquipamentosFields.vue';
import TicketSolicitacaoCandidatos from './TicketSolicitacaoCandidatos.vue';
import TicketSuporteSistema from './TicketSuporteSistema.vue';

const AGENCY_TICKET_TYPE_NAME = 'SOLICITAÇÃO DE CANDIDATOS (AGÊNCIA)';
const SUPORTE_SISTEMA_TICKET_TYPE_NAME = 'SUPORTE SISTEMA (MERUSTRAKER)';

export default {
    name: 'TicketCreateDialog',
    components: {
        TicketCreateGeralFields,
        TicketCreateEquipamentosFields,
        TicketSolicitacaoCandidatos,
        TicketSuporteSistema
    },
    props: {
        value: { type: Boolean, default: false },
        screen: { type: String, default: 'criados' },
        editedItem: { type: Object, required: true },
        categorias: { type: Array, default: () => [] },
        checklists: { type: Array, default: () => [] },
        clientes: { type: Array, default: () => [] },
        useClientes: { type: Boolean, default: true },
        empresaValue: { type: [Number, String], default: null },
        currentUser: { type: Object, default: null },
        displayDate: { type: String, default: null },
        displayTime: { type: String, default: null },
        /** Agendamento é editado só no detalhe, por despachante/superadmin: a criação não mostra por padrão. */
        showDateTimeFields: { type: Boolean, default: false },
        tiposTickets: { type: Array, default: () => [] },
        selectedServiceUsaVeiculo: { type: Boolean, default: false },
        selectedIdEmpresa: { type: [Number, String], default: null },
        selectedServiceUsaEquipamento: { type: Boolean, default: true },
        selectedServiceUsaKit: { type: Boolean, default: false },
        vehicleEquipmentPairs: { type: Array, default: () => [] },
        equipamentosFiltradosPorEmpresa: { type: Array, default: () => [] },
        veiculosFiltradosPorEmpresa: { type: Array, default: () => [] },
        loadingEquipamentos: { type: Boolean, default: false },
        loadingVeiculos: { type: Boolean, default: false },
        vehicleEquipmentPairsValid: { type: Boolean, default: true },
        getEquipamentoDisplayTextById: { type: Function, default: null },
        token: { type: String, default: '' },
        loadingSave: { type: Boolean, default: false },
        csvErrorsOpen: { type: Boolean, default: false }
    },
    data() {
        return {
            formValidado: true,
            gateFormValido: true,
            activeTab: 0
        };
    },
    computed: {
        isAgencyType() {
            const id = this.editedItem && this.editedItem.id_tipo_ticket;
            if (id == null || id === '') return false;
            const row = (this.tiposTickets || []).find(
                (t) => t.id_tipo_ticket === id || Number(t.id_tipo_ticket) === Number(id)
            );
            return !!(row && String(row.nome_tipo_ticket || '').trim() === AGENCY_TICKET_TYPE_NAME);
        },
        isSuporteSistemaType() {
            const id = this.editedItem && this.editedItem.id_tipo_ticket;
            if (id == null || id === '') return false;
            const row = (this.tiposTickets || []).find(
                (t) => t.id_tipo_ticket === id || Number(t.id_tipo_ticket) === Number(id)
            );
            return !!(row && String(row.nome_tipo_ticket || '').trim().toUpperCase() === SUPORTE_SISTEMA_TICKET_TYPE_NAME);
        },
        saveButtonDisabled() {
            if (!this.editedItem || this.editedItem.id_tipo_ticket == null || this.editedItem.id_tipo_ticket === '') {
                return true;
            }
            if (this.isAgencyType || this.isSuporteSistemaType) {
                return this.csvErrorsOpen || this.loadingSave;
            }
            return !this.formValidado || !this.vehicleEquipmentPairsValid || this.csvErrorsOpen || this.loadingSave;
        }
    },
    watch: {
        value(open) {
            if (open) this.activeTab = 0;
        }
    },
    methods: {
        onTipoTicketGateChange(newId) {
            const prev = this.editedItem && this.editedItem.id_tipo_ticket;
            const next = { ...this.editedItem, id_tipo_ticket: newId };
            if (newId !== prev) {
                next.id_tipo_atendimento = null;
                next.id_servico = null;
                next.id_checklist_equipamento = null;
                next.nome_checklist_extenso = null;
            }
            this.$emit('update:editedItem', next);
        },
        onSave() {
            if (!this.editedItem || this.editedItem.id_tipo_ticket == null || this.editedItem.id_tipo_ticket === '') {
                if (this.$refs.gateForm) this.$refs.gateForm.validate();
                return;
            }
            if (this.isAgencyType) {
                const agency = this.$refs.agencyForm;
                if (agency && typeof agency.submit === 'function') {
                    agency.submit();
                }
                return;
            }
            if (this.isSuporteSistemaType) {
                const suporte = this.$refs.suporteSistemaForm;
                if (suporte && typeof suporte.submit === 'function') {
                    suporte.submit();
                }
                return;
            }
            if (this.$refs.form) this.$refs.form.validate();
            if (!this.formValidado) return;
            this.$emit('save');
        },
        onAgencySaved(payload) {
            this.$emit('agency-saved', payload);
            this.$emit('input', false);
        },
        onSuporteSistemaSaved(payload) {
            this.$emit('suporte-sistema-saved', payload);
            this.$emit('agency-saved', payload);
            this.$emit('input', false);
        }
    }
};
</script>
